import { type MaintenanceInkStroke, type MaintenanceInkText } from '@gb-ticket/shared-types';

const PREFIX = 'maintenance-protocol-ink:';

export type ProtocolInkPageDraft = {
  strokes: MaintenanceInkStroke[];
  texts: MaintenanceInkText[];
};

export type ProtocolInkDraft = Record<string, ProtocolInkPageDraft>;

export function emptyInkPageDraft(): ProtocolInkPageDraft {
  return { strokes: [], texts: [] };
}

export function normalizeInkPageDraft(raw: unknown): ProtocolInkPageDraft | null {
  if (Array.isArray(raw)) {
    return { strokes: raw as MaintenanceInkStroke[], texts: [] };
  }
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const value = raw as { strokes?: unknown; texts?: unknown };
  if (!Array.isArray(value.strokes)) {
    return null;
  }
  return {
    strokes: value.strokes as MaintenanceInkStroke[],
    texts: Array.isArray(value.texts) ? (value.texts as MaintenanceInkText[]) : [],
  };
}

export function readInkDraft(protocolId: string): ProtocolInkDraft {
  try {
    const raw = sessionStorage.getItem(`${PREFIX}${protocolId}`);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }
    const draft: ProtocolInkDraft = {};
    for (const [key, value] of Object.entries(parsed)) {
      const page = normalizeInkPageDraft(value);
      if (page) {
        draft[key] = page;
      }
    }
    return draft;
  } catch {
    return {};
  }
}

export function writeInkDraft(protocolId: string, draft: ProtocolInkDraft): void {
  sessionStorage.setItem(`${PREFIX}${protocolId}`, JSON.stringify(draft));
}

export function writeInkPageDraft(
  protocolId: string,
  page: number,
  state: ProtocolInkPageDraft,
): void {
  const draft = readInkDraft(protocolId);
  draft[String(page)] = {
    strokes: state.strokes,
    texts: state.texts,
  };
  writeInkDraft(protocolId, draft);
}

export function clearInkPageDraft(protocolId: string, page: number): void {
  const draft = readInkDraft(protocolId);
  delete draft[String(page)];
  if (Object.keys(draft).length === 0) {
    sessionStorage.removeItem(`${PREFIX}${protocolId}`);
    return;
  }
  writeInkDraft(protocolId, draft);
}

export function clearInkDraft(protocolId: string): void {
  sessionStorage.removeItem(`${PREFIX}${protocolId}`);
}
